import { useEffect } from 'react';

/**
 * index.html ships static SEO default tags (description, Open Graph, Twitter)
 * plus a runtime script that absolutizes og:image / og:url at first load.
 * react-helmet-async APPENDS page-specific tags without removing those
 * defaults, so crawlers see two competing tags per key and typically honor
 * the first — the generic or stale one.
 *
 * Static defaults are marked with `data-static-seo` in index.html. Whenever a
 * page-specific (unmarked) tag exists for the same key, remove the static
 * one. Non-JS crawlers still read the static tags from the raw HTML; only
 * JS-executing clients reach this code.
 */
const SEO_TAG_SELECTORS = [
  'meta[name="description"]',
  'meta[property="og:url"]',
  'meta[property="og:title"]',
  'meta[property="og:description"]',
  'meta[property="og:type"]',
  'meta[property="og:image"]',
  'meta[property="og:site_name"]',
  'meta[name="twitter:card"]',
  'meta[name="twitter:title"]',
  'meta[name="twitter:description"]',
  'meta[name="twitter:image"]',
];

function dedupeStaticMeta(): void {
  for (const selector of SEO_TAG_SELECTORS) {
    const tags = document.querySelectorAll(selector);
    let hasPageSpecific = false;
    tags.forEach((tag) => {
      if (!tag.hasAttribute('data-static-seo')) hasPageSpecific = true;
    });
    if (!hasPageSpecific) continue;
    tags.forEach((tag) => {
      if (tag.hasAttribute('data-static-seo')) tag.remove();
    });
  }
}

export function useDedupeStaticMeta(): void {
  useEffect(() => {
    dedupeStaticMeta();

    // Helmet mutates the head as pages mount/unmount; re-check on each change.
    const observer = new MutationObserver(dedupeStaticMeta);
    observer.observe(document.head, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
}
